import React from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { SLASH_COMMANDS, filterCommands, type SlashCommand } from './slashCommands';

const TIER_LABELS: Record<number, string> = {
  1: 'Essentials',
  2: 'Analytics',
  3: 'Format',
};

const TIER_ACCENT: Record<number, string> = {
  1: 'text-brand-purple-light',
  2: 'text-brand-teal-light',
  3: 'text-amber-400',
};

interface SlashCommandPickerProps {
  open: boolean;
  query: string;
  selectedIndex: number;
  onSelect: (cmd: SlashCommand) => void;
}

export default function SlashCommandPicker({
  open,
  query,
  selectedIndex,
  onSelect,
}: SlashCommandPickerProps) {
  const filtered = query === '' ? SLASH_COMMANDS : filterCommands(query);

  // Group by tier when showing all; flat list when filtering
  const grouped = query === ''
    ? ([1, 2, 3] as const).map((tier) => ({
        tier,
        label: TIER_LABELS[tier],
        items: filtered.filter((c) => c.tier === tier),
      })).filter((g) => g.items.length > 0)
    : [{ tier: 0 as any, label: '', items: filtered }];

  // Build flat ordered list to resolve selectedIndex
  const flat = grouped.flatMap((g) => g.items);

  return (
    <AnimatePresence>
      {open && flat.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.12, ease: 'easeOut' }}
          className="absolute bottom-full left-0 right-0 z-30 mb-2 max-h-72 overflow-y-auto rounded-xl border border-dark-border bg-dark-surface shadow-xl shadow-black/60"
          role="listbox"
          aria-label="Slash commands"
        >
          {grouped.map((group) => (
            <div key={group.tier}>
              {group.label ? (
                <p className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
                  {group.label}
                </p>
              ) : null}
              {group.items.map((cmd) => {
                const globalIdx = flat.indexOf(cmd);
                const isSelected = globalIdx === selectedIndex;
                const Icon = cmd.icon;
                const accent = TIER_ACCENT[cmd.tier] || 'text-slate-400';
                return (
                  <button
                    key={cmd.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(e) => {
                      e.preventDefault(); // keep focus on textarea
                      onSelect(cmd);
                    }}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                      isSelected
                        ? 'bg-dark-elevated text-white'
                        : 'text-slate-300 hover:bg-dark-base hover:text-white'
                    }`}
                  >
                    <Icon
                      className={`h-3.5 w-3.5 shrink-0 ${isSelected ? accent : 'text-slate-500'}`}
                      aria-hidden
                    />
                    <span className={`w-24 shrink-0 font-mono text-xs ${isSelected ? accent : 'text-slate-400'}`}>
                      {cmd.label}
                    </span>
                    <span className="truncate text-xs text-slate-500">{cmd.description}</span>
                    {cmd.action.type === 'format' && (
                      <span className="ml-auto shrink-0 rounded-full bg-amber-900/30 px-2 py-0.5 text-[10px] text-amber-400">
                        modifier
                      </span>
                    )}
                    {cmd.action.type === 'ui' && (
                      <span className="ml-auto shrink-0 rounded-full bg-dark-base px-2 py-0.5 text-[10px] text-slate-500">
                        action
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
